import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lead,
  LeadBiddingAnalysis,
  EnhancedDeal,
  BiddingTimberItem,
  BiddingTrainingExample,
  BiddingTrainingLabel,
} from '../../types';
import { useAuth } from '../../utils/AuthContext';
import {
  requestBiddingAgentAnalysis,
  subscribeBiddingAnalysisRequest,
  readServerBiddingAnalysis,
  formatCurrency,
  getQualificationColor,
  getQualificationLabel,
  type BiddingAnalysisRequestSnapshot,
} from '../../utils/biddingAgentService';
import {
  biddingItemsFromStructures,
  draftStructuresFromTakeoff,
  takeoffStructuresFromAnalysis,
  timberListItemsFromAnalysis,
} from '../../utils/biddingProposalTakeoff';
import { pushTakeoffToProposal } from '../../utils/timberTakeoffHelpers';
import {
  subscribeLeadTrainingExamples,
  recordStaffTrainingExample,
  labelFromTimberItem,
} from '../../utils/biddingTrainingExampleService';
import {
  Loader2,
  Calculator,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  HelpCircle,
  MapPin,
  GraduationCap,
  Check,
  Pencil,
} from 'lucide-react';
import './BiddingAnalysisPanel.css';

interface BiddingAnalysisPanelProps {
  lead: Lead;
  deals: EnhancedDeal[];
  /** When set, timber rows with a linked document/pin show a "View on plan" action. */
  onViewOnPlan?: (item: BiddingTimberItem) => void;
}

function parseFirestoreDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const d = (value as { toDate: () => Date }).toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? null : d;
}

function getTimeAgo(date: Date | null): string {
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export const BiddingAnalysisPanel: React.FC<BiddingAnalysisPanelProps> = ({ lead, deals, onViewOnPlan }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState<LeadBiddingAnalysis | null>(lead.biddingAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestSnap, setRequestSnap] = useState<BiddingAnalysisRequestSnapshot | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<string>(
    deals[0]?.id || lead.convertedToDeal || '',
  );
  const [trainingExamples, setTrainingExamples] = useState<BiddingTrainingExample[]>([]);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState<BiddingTrainingLabel>({});
  const [savingTrainingId, setSavingTrainingId] = useState<string | null>(null);
  const [applyingTakeoff, setApplyingTakeoff] = useState(false);
  const [applyTakeoffMessage, setApplyTakeoffMessage] = useState<string | null>(null);

  useEffect(() => {
    if (lead.biddingAnalysis) {
      setAnalysis(lead.biddingAnalysis);
    }
  }, [lead.biddingAnalysis]);

  useEffect(() => {
    if (!lead.id) return;
    const unsub = subscribeLeadTrainingExamples(lead.id, setTrainingExamples);
    return () => unsub();
  }, [lead.id]);

  // Live status of the background analysis request — survives closing/reopening.
  useEffect(() => {
    if (!lead.id) return;
    return subscribeBiddingAnalysisRequest(lead.id, setRequestSnap);
  }, [lead.id]);

  // When the background run finishes, pull the freshly written analysis.
  useEffect(() => {
    if (requestSnap?.status !== 'complete' || !lead.id) return;
    let cancelled = false;
    readServerBiddingAnalysis(lead.id).then(result => {
      if (!cancelled && result) setAnalysis(result);
    });
    return () => {
      cancelled = true;
    };
  }, [requestSnap?.status, requestSnap?.completedAt, lead.id]);

  useEffect(() => {
    if (!selectedDealId && deals.length > 0 && deals[0]?.id) {
      setSelectedDealId(deals[0].id!);
    }
  }, [deals, selectedDealId]);

  // Kick off the analysis in the background: queue the request and return.
  // The Mac-mini agent processes it independently, so the user can close the
  // window; the request-status subscription drives the UI when they return.
  const handleAnalyze = useCallback(async () => {
    if (!lead.id) return;
    setLoading(true);
    setError(null);
    try {
      await requestBiddingAgentAnalysis(lead.id, {
        dealId: selectedDealId || undefined,
        includeDocuments: true,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to queue bidding analysis');
    } finally {
      setLoading(false);
    }
  }, [lead.id, selectedDealId]);

  const analysisId = useMemo(() => {
    const d = parseFirestoreDate(analysis?.analyzedAt);
    return d ? d.toISOString() : 'current';
  }, [analysis?.analyzedAt]);

  const trainedItemIds = useMemo(() => {
    const set = new Set<string>();
    for (const ex of trainingExamples) {
      if (ex.agentItem?.id) set.add(ex.agentItem.id);
    }
    return set;
  }, [trainingExamples]);

  const confirmTimberItem = useCallback(
    async (item: BiddingTimberItem) => {
      if (!lead.id || !user) return;
      setSavingTrainingId(item.id);
      try {
        await recordStaffTrainingExample({
          leadId: lead.id,
          analysisId,
          agentItem: item,
          label: labelFromTimberItem(item),
          exampleType: 'staffConfirmation',
          userId: user.id,
          userName: user.name || user.email,
        });
      } finally {
        setSavingTrainingId(null);
      }
    },
    [lead.id, user, analysisId],
  );

  const startCorrecting = useCallback((item: BiddingTimberItem) => {
    setEditingItemId(item.id);
    setEditLabel(labelFromTimberItem(item));
  }, []);

  const saveCorrection = useCallback(
    async (item: BiddingTimberItem) => {
      if (!lead.id || !user) return;
      setSavingTrainingId(item.id);
      try {
        await recordStaffTrainingExample({
          leadId: lead.id,
          analysisId,
          agentItem: item,
          label: editLabel,
          exampleType: 'staffCorrection',
          userId: user.id,
          userName: user.name || user.email,
        });
        setEditingItemId(null);
      } finally {
        setSavingTrainingId(null);
      }
    },
    [lead.id, user, analysisId, editLabel],
  );

  const reqStatus = requestSnap?.status ?? null;
  const isRunning = loading || reqStatus === 'pending' || reqStatus === 'processing';
  const reqFailed = reqStatus === 'failed';
  const runStartedAt = requestSnap?.startedAt || requestSnap?.requestedAt || null;

  const runningCard = (
    <div className="bap2-bg-running">
      <Loader2 size={20} className="bap2-spin bap2-bg-icon" />
      <span className="bap2-bg-title">
        {reqStatus === 'processing' ? 'Bidding agent is analyzing…' : 'Queued for the bidding agent…'}
      </span>
      <p className="bap2-bg-hint">
        Running in the background — you can close this window and come back later. The results
        will appear here automatically when the agent finishes.
      </p>
      {runStartedAt && <span className="bap2-bg-meta">Started {getTimeAgo(runStartedAt)}</span>}
    </div>
  );

  if (!analysis) {
    if (isRunning) return runningCard;
    if (error || reqFailed) {
      return (
        <div className="bap2-error">
          <AlertTriangle size={14} />
          <span>{error || requestSnap?.error || 'Bidding analysis failed'}</span>
          <button onClick={handleAnalyze} className="bap2-retry-btn">Retry</button>
        </div>
      );
    }
    return (
      <div className="bap2-empty">
        <Calculator size={22} className="bap2-empty-icon" />
        <span className="bap2-empty-text">Bidding Analysis</span>
        <p className="bap2-empty-hint">
          Run qualification, cost estimate, timber extraction, and proposal prep via the Mac-mini bidding agent.
          It runs in the background, so you can close this window and return for the results.
        </p>
        {deals.length > 1 && (
          <label className="bap2-deal-picker">
            <span>Deal for proposal context</span>
            <select
              value={selectedDealId}
              onChange={e => setSelectedDealId(e.target.value)}
            >
              {deals.map(d => (
                <option key={d.id} value={d.id}>
                  {d.title || d.id}
                </option>
              ))}
            </select>
          </label>
        )}
        <button className="bap2-run-btn" onClick={handleAnalyze} disabled={loading}>
          <Calculator size={13} />
          {loading ? 'Starting…' : 'Run Bidding Analysis'}
        </button>
      </div>
    );
  }

  const analyzedDate = parseFirestoreDate(analysis.analyzedAt);
  const qual = analysis.qualification;
  const cost = analysis.costEstimate;
  const timber = analysis.timberExtraction;
  const prep = analysis.proposalPrep;
  const proposalDealId = prep?.dealId || selectedDealId || deals[0]?.id;
  const timberItems = timber
    ? (timber.items?.length ? timber.items : biddingItemsFromStructures(timber))
    : [];
  const takeoffStructures = takeoffStructuresFromAnalysis(analysis);
  const proposalTimberRows = timberListItemsFromAnalysis(analysis);
  const hasProposalTakeoff = proposalTimberRows.length > 0;

  const hasCostRange = Number.isFinite(cost?.grandTotalLow) && Number.isFinite(cost?.grandTotalHigh);
  const bucketEntries: [string, number][] = cost?.buckets
    ? (Object.entries(cost.buckets) as [string, number][]).filter(([, v]) => Number.isFinite(v) && v > 0)
    : [];
  const hasCost = !!cost && (hasCostRange || bucketEntries.length > 0 || (cost.assumptions?.length ?? 0) > 0);

  const prepStructures = Number.isFinite(prep?.suggestedStructureCount) ? prep!.suggestedStructureCount : null;
  const prepType = prep?.kitRecommendation && prep.kitRecommendation !== 'unknown' ? prep.kitRecommendation : null;
  const hasPrep = !!prep && (
    prepStructures != null ||
    prepType != null ||
    (prep.nodeSetHints?.length ?? 0) > 0 ||
    (prep.nextSteps?.length ?? 0) > 0
  );

  const hasContent = !!(qual || hasCost || timberItems.length > 0 || hasProposalTakeoff || hasPrep);

  const handleApplyTakeoffToProposal = async () => {
    if (!lead.id || !proposalDealId || !user || proposalTimberRows.length === 0) return;
    setApplyingTakeoff(true);
    setApplyTakeoffMessage(null);
    try {
      const result = await pushTakeoffToProposal(
        proposalDealId,
        lead.id,
        proposalTimberRows,
        draftStructuresFromTakeoff(takeoffStructures, {
          dealId: proposalDealId,
          leadId: lead.id,
          userId: user.id,
          userName: user.name || user.email || 'staff',
        }),
        {
          userId: user.id,
          contactName: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
          projectName: lead.company || lead.projectType || 'project',
        },
      );
      setApplyTakeoffMessage(result.message);
      if (result.ok) navigate(`/proposal-builder/${proposalDealId}`);
    } catch (e) {
      setApplyTakeoffMessage(e instanceof Error ? e.message : 'Failed to apply take-off');
    } finally {
      setApplyingTakeoff(false);
    }
  };

  return (
    <div className="bap2-root">
      {isRunning && (
        <div className="bap2-running-banner">
          <Loader2 size={13} className="bap2-spin" />
          <span>
            {reqStatus === 'processing' ? 'Re-analyzing in the background' : 'Queued for re-analysis'}
            {' — you can close this window and come back.'}
          </span>
        </div>
      )}
      {!isRunning && reqFailed && (
        <div className="bap2-running-banner bap2-running-banner-error">
          <AlertTriangle size={13} />
          <span>Last background run failed{requestSnap?.error ? `: ${requestSnap.error}` : ''}.</span>
        </div>
      )}
      <div className="bap2-hero">
        {qual ? (
          <>
            <div
              className="bap2-rec-badge"
              style={{ background: getQualificationColor(qual.recommendation) }}
            >
              {getQualificationLabel(qual.recommendation)}
            </div>
            <div className="bap2-hero-info">
              <div className="bap2-hero-title">Qualification</div>
              <div className="bap2-hero-meta">
                {[
                  Number.isFinite(qual.budgetFitScore) ? `Budget fit ${qual.budgetFitScore}/100` : null,
                  getTimeAgo(analyzedDate),
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
          </>
        ) : (
          <div className="bap2-hero-info">
            <div className="bap2-hero-title">Bidding Analysis</div>
            <div className="bap2-hero-meta">{getTimeAgo(analyzedDate)}</div>
          </div>
        )}
        <button
          className="bap2-refresh"
          onClick={handleAnalyze}
          disabled={isRunning}
          title={isRunning ? 'Analysis running in the background…' : 'Re-run analysis'}
        >
          {isRunning ? <Loader2 size={12} className="bap2-spin" /> : <RefreshCw size={12} />}
        </button>
      </div>

      {analysis.agentNotes && (
        <div className="bap2-notes">{analysis.agentNotes}</div>
      )}

      {!hasContent && (
        <div className="bap2-sparse">
          <Calculator size={18} className="bap2-sparse-icon" />
          <p className="bap2-sparse-text">
            Analysis record created, but the bidding agent hasn&apos;t returned qualification, cost,
            timber, or proposal data yet. Re-run once the Mac-mini agent is online.
          </p>
          <button className="bap2-retry-btn" onClick={handleAnalyze} disabled={isRunning}>
            {isRunning ? <Loader2 size={12} className="bap2-spin" /> : <RefreshCw size={12} />}
            {isRunning ? ' Running…' : ' Run analysis'}
          </button>
        </div>
      )}

      {qual && (
        <section className="bap2-section">
          <h4 className="bap2-section-title">Qualification</h4>
          {qual.summary && <p className="bap2-summary">{qual.summary}</p>}
          {qual.feasibilityFlags && qual.feasibilityFlags.length > 0 && (
            <ul className="bap2-flag-list">
              {qual.feasibilityFlags.map((flag, i) => (
                <li key={i} className="bap2-flag bap2-flag-positive">
                  <CheckCircle2 size={12} />
                  {flag}
                </li>
              ))}
            </ul>
          )}
          {qual.riskNotes && qual.riskNotes.length > 0 && (
            <ul className="bap2-flag-list">
              {qual.riskNotes.map((note, i) => (
                <li key={i} className="bap2-flag bap2-flag-risk">
                  <XCircle size={12} />
                  {note}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {hasCost && cost && (
        <section className="bap2-section">
          <h4 className="bap2-section-title">Cost Estimate</h4>
          {hasCostRange && (
            <div className="bap2-cost-range">
              {formatCurrency(cost.grandTotalLow)} – {formatCurrency(cost.grandTotalHigh)}
            </div>
          )}
          {bucketEntries.length > 0 && (
            <table className="bap2-bucket-table">
              <tbody>
                {bucketEntries.map(([key, val]) => (
                  <tr key={key}>
                    <td>{key.charAt(0).toUpperCase() + key.slice(1)}</td>
                    <td>{formatCurrency(val)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {cost.assumptions && cost.assumptions.length > 0 && (
            <ul className="bap2-assumptions">
              {cost.assumptions.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {timber && (timberItems.length > 0 || hasProposalTakeoff) && (
        <section className="bap2-section">
          <h4 className="bap2-section-title">
            Timber Extraction
            {(timber.documentCount ?? 0) > 0 && (
              <span className="bap2-section-meta">{timber.documentCount} doc(s)</span>
            )}
          </h4>
          {timber.summary && <p className="bap2-summary">{timber.summary}</p>}
          {hasProposalTakeoff && (
            <div className="bap2-takeoff-apply">
              <p className="bap2-takeoff-apply-hint">
                {proposalTimberRows.length} line{proposalTimberRows.length === 1 ? '' : 's'} in proposal
                timber-list shape (species, grade, HxWxL feet).
              </p>
              {proposalDealId ? (
                <button
                  type="button"
                  className="bap2-proposal-link"
                  disabled={applyingTakeoff || !user}
                  onClick={() => void handleApplyTakeoffToProposal()}
                >
                  {applyingTakeoff ? <Loader2 size={13} className="bap2-spin" /> : <ExternalLink size={13} />}
                  {applyingTakeoff ? 'Applying…' : 'Apply take-off to proposal'}
                </button>
              ) : (
                <p className="bap2-no-deal">
                  <HelpCircle size={12} />
                  Create a deal to apply this take-off to Proposal Builder.
                </p>
              )}
              {applyTakeoffMessage && <p className="bap2-takeoff-apply-msg">{applyTakeoffMessage}</p>}
            </div>
          )}
          <div className="bap2-train-banner">
            <GraduationCap size={13} />
            <span>
              Confirm or correct extracted timber to train the bidding agent.
              {trainedItemIds.size > 0 && (
                <strong> {trainedItemIds.size}/{timberItems.length} reviewed</strong>
              )}
            </span>
          </div>
          <div className="bap2-timber-scroll">
            <table className="bap2-timber-table">
              <thead>
                <tr>
                  <th>Structure</th>
                  <th>Qty</th>
                  <th>Dimensions</th>
                  <th>Species</th>
                  <th>Name</th>
                  <th aria-label="Train" className="bap2-train-col">Train</th>
                  {onViewOnPlan && <th aria-label="Plan link" />}
                </tr>
              </thead>
              <tbody>
                {timberItems.map(item => {
                  const hasPlanRef = !!(item.documentId && (item.annotationId || item.pageNumber != null));
                  const trained = trainedItemIds.has(item.id);
                  const editing = editingItemId === item.id;
                  const saving = savingTrainingId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className={trained ? 'bap2-row-trained' : undefined}>
                        <td>{item.structureName || '—'}</td>
                        <td>{item.quantity}</td>
                        <td>
                          {item.dimensions
                            || (item.width && item.height
                              ? `${item.width}x${item.height}${item.length ? ` × ${item.length}` : ''}`
                              : '—')}
                        </td>
                        <td>{item.species || item.material || '—'}</td>
                        <td>{item.name}</td>
                        <td className="bap2-train-cell">
                          {trained ? (
                            <span className="bap2-trained-chip" title="Saved as training example">
                              <GraduationCap size={11} /> Trained
                            </span>
                          ) : (
                            <span className="bap2-train-actions">
                              <button
                                type="button"
                                className="bap2-train-confirm"
                                disabled={saving || !user}
                                onClick={() => confirmTimberItem(item)}
                                title="Confirm as correct"
                              >
                                {saving ? <Loader2 size={11} className="bap2-spin" /> : <Check size={11} />}
                              </button>
                              <button
                                type="button"
                                className="bap2-train-correct"
                                disabled={saving || !user}
                                onClick={() => startCorrecting(item)}
                                title="Correct values"
                              >
                                <Pencil size={11} />
                              </button>
                            </span>
                          )}
                        </td>
                        {onViewOnPlan && (
                          <td className="bap2-timber-plan-cell">
                            {hasPlanRef && (
                              <button
                                type="button"
                                className="bap2-view-on-plan"
                                onClick={() => onViewOnPlan(item)}
                                title="View on plan"
                              >
                                <MapPin size={12} />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {editing && (
                        <tr className="bap2-train-edit-row">
                          <td colSpan={onViewOnPlan ? 7 : 6}>
                            <div className="bap2-train-edit">
                              <label>
                                <span>Name</span>
                                <input
                                  value={editLabel.name || ''}
                                  onChange={e => setEditLabel(l => ({ ...l, name: e.target.value }))}
                                />
                              </label>
                              <label>
                                <span>Dimensions</span>
                                <input
                                  value={editLabel.dimensions || ''}
                                  onChange={e => setEditLabel(l => ({ ...l, dimensions: e.target.value }))}
                                  placeholder="8x8"
                                />
                              </label>
                              <label>
                                <span>Qty</span>
                                <input
                                  type="number"
                                  value={editLabel.quantity ?? ''}
                                  onChange={e =>
                                    setEditLabel(l => ({
                                      ...l,
                                      quantity: e.target.value === '' ? undefined : Number(e.target.value),
                                    }))
                                  }
                                />
                              </label>
                              <label>
                                <span>Species</span>
                                <input
                                  value={editLabel.species || ''}
                                  onChange={e => setEditLabel(l => ({ ...l, species: e.target.value }))}
                                />
                              </label>
                              <div className="bap2-train-edit-actions">
                                <button
                                  type="button"
                                  className="bap2-train-save"
                                  disabled={saving}
                                  onClick={() => saveCorrection(item)}
                                >
                                  {saving ? <Loader2 size={12} className="bap2-spin" /> : 'Save correction'}
                                </button>
                                <button
                                  type="button"
                                  className="bap2-train-cancel"
                                  onClick={() => setEditingItemId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {hasPrep && prep && (
        <section className="bap2-section">
          <h4 className="bap2-section-title">Proposal Prep</h4>
          {(prepStructures != null || prepType != null) && (
            <div className="bap2-prep-grid">
              {prepStructures != null && (
                <div className="bap2-prep-stat">
                  <span className="bap2-prep-label">Structures</span>
                  <span className="bap2-prep-value">{prepStructures}</span>
                </div>
              )}
              {prepType != null && (
                <div className="bap2-prep-stat">
                  <span className="bap2-prep-label">Type</span>
                  <span className="bap2-prep-value">{prepType}</span>
                </div>
              )}
            </div>
          )}
          {prep.nodeSetHints && prep.nodeSetHints.length > 0 && (
            <ul className="bap2-hints">
              {prep.nodeSetHints.map((hint, i) => (
                <li key={i}>{hint}</li>
              ))}
            </ul>
          )}
          {prep.nextSteps && prep.nextSteps.length > 0 && (
            <ol className="bap2-steps">
              {prep.nextSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          )}
          {proposalDealId ? (
            <button
              type="button"
              className="bap2-proposal-link"
              onClick={() => navigate(`/proposal-builder/${proposalDealId}`)}
            >
              <ExternalLink size={13} />
              Open Proposal Builder
            </button>
          ) : (
            <p className="bap2-no-deal">
              <HelpCircle size={12} />
              Create a deal to link proposal prep actions.
            </p>
          )}
        </section>
      )}

      {analysis.dataSources && analysis.dataSources.length > 0 && (
        <div className="bap2-sources">
          {analysis.dataSources.map(src => (
            <span key={src} className="bap2-src-tag">{src}</span>
          ))}
        </div>
      )}
    </div>
  );
};
